import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Grid
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { useActionSettings } from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';

const SettingCreatePdf = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  
  // PDF specific settings
  const [targetPath, setTargetPath] = useState('');
  const [contentType, setContentType] = useState('custom'); // 'custom', 'generated'
  const [customContent, setCustomContent] = useState('');
  const [genContent, setGenContent] = useState('');
  
  // Search and menus
  const [anchorEl, setAnchorEl] = useState(null); 
  const [menuType, setMenuType] = useState(''); 
  
  // Refs for TagEditors
  const targetRef = useRef(null);
  const contentRef = useRef(null);
  const genRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  // Use unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    id,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    ws
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'createpdf',
    onSaveStatusChange,
    customFields: {
      target: '/{filename}.pdf',
      contentType: 'custom',
      customContent: '',
      genContent: ''
    },
    customComparison: (initial) => {
      return (
        initial.target === targetPath &&
        initial.contentType === contentType &&
        initial.customContent === customContent &&
        initial.genContent === genContent
      );
    },
    customSaveData: () => ({
      target: targetPath,
      contentType,
      useCustomContent: contentType === 'custom',
      customContent,
      useGenContent: contentType === 'generated',
      genContent
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (e) {
          console.error('Failed to parse data JSON:', e);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }
      
      setTargetPath(parsedData.target || '');
      const loadedContentType = parsedData.contentType || 'custom';
      setContentType(loadedContentType);
      setCustomContent(parsedData.customContent || '');
      setGenContent(parsedData.genContent || '');
    },
    enableConditions: true
  });

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    ws,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        let folderPathClean = folderPath.replace(/\/+$/, "");
        const separator = folderPathClean.includes('\\') ? '\\' : '/';
        const newPath = folderPathClean + separator + "{filename}.pdf";
        setTargetPath(newPath);
      }
    }
  });

  const customDataTags = useMemo(() => [{ 
    key: 'tag_fileContentToWrite', 
    label: 'Data Target File Content (if exist)', 
    value: 'fileContentToWrite', 
    description: 'Content of the target file if it already exists' 
  }], []);

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
  };
    
  const handleTagSelect = (value) => {
    if (menuType === 'target') {
      targetRef.current?.insertTag(value);
    } else if (menuType === 'content') {
      contentRef.current?.insertTag(value);
    } else if (menuType === 'gen') {
      genRef.current?.insertTag(value);
    }
    handleMenuClose();
  };

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('createpdf', 'Create PDF')}
      isSaveDisabled={isSaveDisabled}
      onSave={baseHandleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
    >
      <Grid container spacing={3}>
        {/* Target Path Section */}
        <Grid item xs={12}>
          <Card sx={{
            mb: 2,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#5757b0',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                  }}>
                    {t('WHERE', 'WHERE')}
                  </Box>
                  {t('target_path', 'Target Document Path')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <TagEditor
                ref={targetRef}
                value={targetPath}
                onChange={setTargetPath}
                placeholder={t('enter_target_path_pdf', 'Enter target path for PDF document...')}
                singleLine
                onTagClick={(e) => handleMenuOpen(e, 'target')}
                tags={tags}
              />

              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 1,
                mt: 1.5,
              }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleBrowseDirectory}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>


        {/* Content Options */}
        <Grid item xs={12}>
          <Card sx={{
            mb: 2,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#5757b0',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                  }}>
                    {t('CONTENT', 'CONTENT')}
                  </Box>
                  {t('document_content', 'Document Content')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend" sx={{
                  fontWeight: 600,
                  color: '#444',
                  fontSize: '0.95rem',
                  mb: 1,
                }}>
                  {t('create_pdf.content_source', 'Content Source')}
                </FormLabel>
                <RadioGroup
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  sx={{ mt: 1 }}
                >
                  <FormControlLabel 
                    value="custom" 
                    control={<Radio />} 
                    label={t('create_pdf.custom_content_option', 'Custom Content')}
                    sx={{ mb: 1 }}
                  />
                  <FormControlLabel 
                    value="generated" 
                    control={<Radio />} 
                    label={t('create_pdf.ai_generated_option', 'AI Generated Content')}
                  />
                </RadioGroup>
              </FormControl>

              {/* Custom Content Section */}
              {contentType === 'custom' && (
                <Box sx={{ ml: 0, mt: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#444', mb: 1 }}>
                    {t('custom_content', 'Custom Content')}
                  </Typography>
                  <TagEditor
                    ref={contentRef}
                    value={customContent}
                    onChange={setCustomContent}
                    multiline
                    minRows={6}
                    placeholder={t('enter_custom_content_pdf', 'Enter custom content for the PDF document. Use # for headings...')}
                    onTagClick={(e) => handleMenuOpen(e, 'content')}
                    tags={tags}
                  />
                </Box>
              )}

              {/* Generative Content Section */}
              {contentType === 'generated' && (
                <Box sx={{ ml: 0, mt: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#444', mb: 1 }}>
                    {t('generation_prompt', 'Generation Prompt')}
                  </Typography>
                  <TagEditor
                    ref={genRef}
                    value={genContent}
                    onChange={setGenContent}
                    multiline
                    minRows={6}
                    placeholder={t('enter_prompt_pdf', 'Enter prompt for AI to generate PDF content...')}
                    tags={tags}
                    onTagClick={(e) => handleMenuOpen(e, 'gen')}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {FolderBrowserComponent}
      
      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMenuClose}
        additionalTags={customDataTags}
        menuType={menuType}
      />
    </ActionSettingsLayout>
  );
};

export default React.memo(SettingCreatePdf);
